import { cn } from '@/shared/helpers/classnames'
import { getTypedBlocksByTypes } from '@/shared/helpers/compoundPattern'

import {
  Children,
  type FC,
  type ReactElement,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useMemo,
} from 'react'

import { Root as RadixAccordionRoot } from '@radix-ui/react-accordion'

import { BlockContext, type BlockContextValue } from './BlockContext'
import { BlockItem } from './BlockItem'
import type {
  BlockAccordionProps,
  BlockContentVariant,
  BlockItemBlocksTypes,
  BlockLayoutVariant,
  BlockProps,
  BlockSize,
} from './types'

/**
 * Context to detect if Block is inside a Block.List
 */
export const BlockListContext = createContext(false)

/**
 * Block component - Can be used standalone or inside Block.List
 *
 * When used standalone, automatically wraps itself in a Block.List with a single item.
 * When used inside Block.List, behaves as an accordion item.
 *
 * @example Standalone block (auto-wrapped)
 * <Block>
 *   <Block.Title>General information</Block.Title>
 *   <Block.Content>
 *     <p>Block content</p>
 *   </Block.Content>
 * </Block>
 *
 * @example Static block (non-collapsible)
 * <Block collapsible={false}>
 *   <Block.Title>General information</Block.Title>
 *   <Block.Content>
 *     <p>Block content</p>
 *   </Block.Content>
 * </Block>
 *
 * @example Multiple blocks
 * <Block.List>
 *   <Block>
 *     <Block.Title>Section 1</Block.Title>
 *     <Block.Content>Content 1</Block.Content>
 *   </Block>
 *   <Block>
 *     <Block.Title>Section 2</Block.Title>
 *     <Block.Content>Content 2</Block.Content>
 *   </Block>
 * </Block.List>
 */
export const BlockMain = forwardRef<HTMLDivElement, BlockProps>(
  (
    {
      children,
      contentVariant,
      size,
      layoutVariant = 'separated',
      collapsible = true,
      defaultOpen = false,
      ...otherProps
    },
    ref,
  ) => {
    const inList = useContext(BlockListContext)

    const blocks = getTypedBlocksByTypes<BlockItemBlocksTypes>(children)

    // Extract value from otherProps (injected internally by normalizeChildren)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value = (otherProps as any).value

    // Static blocks (collapsible=false) should be open by default
    const effectiveCollapsible = blocks.BlockContent ? collapsible : false
    const effectiveDefaultOpen = effectiveCollapsible === false ? true : defaultOpen

    // If inside Block.List, render as BlockItem
    if (inList) {
      return (
        <BlockItem
          ref={ref}
          disabled={!effectiveCollapsible}
          contentVariant={contentVariant}
          value={value}
        >
          {children}
        </BlockItem>
      )
    }

    // Otherwise, wrap in Block.List automatically
    return (
      <BlockListRoot size={size} layoutVariant={layoutVariant} allowMultiple={false}>
        <BlockItem
          ref={ref}
          value="single-item"
          disabled={!effectiveCollapsible}
          defaultOpen={effectiveDefaultOpen}
          contentVariant={contentVariant}
        >
          {children}
        </BlockItem>
      </BlockListRoot>
    )
  },
)

BlockMain.displayName = 'BlockMain'

/**
 * Block.List component - Container for multiple Block items
 *
 * Uses Radix UI for accessibility and Compound Pattern.
 * Supports two modes:
 * - allowMultiple={false}: only one collapsible block can be open at a time (default)
 * - allowMultiple={true}: multiple collapsible blocks can be open simultaneously
 *
 * Note: Static blocks (collapsible={false}) and collapsible blocks can be mixed in the same list.
 * The initial open state is managed via defaultOpen prop on each Block.
 *
 * @example Single selection mode (default)
 * <Block.List>
 *   <Block>
 *     <Block.Title>Section 1</Block.Title>
 *     <Block.Content>Content</Block.Content>
 *   </Block>
 *   <Block defaultOpen>
 *     <Block.Title>Section 2</Block.Title>
 *     <Block.Content>Initially open</Block.Content>
 *   </Block>
 * </Block.List>
 *
 * @example Multiple selection mode
 * <Block.List allowMultiple>
 *   <Block defaultOpen>
 *     <Block.Title>Section 1</Block.Title>
 *     <Block.Content>Initially open</Block.Content>
 *   </Block>
 *   <Block defaultOpen>
 *     <Block.Title>Section 2</Block.Title>
 *     <Block.Content>Also initially open</Block.Content>
 *   </Block>
 * </Block.List>
 */
type BlockAccordionInternalProps = BlockAccordionProps

type NormalizedChild = {
  element: ReactElement
  value: string
  defaultOpen: boolean
}

/**
 * Normalize children to ensure each Block has an auto-generated value and extract defaultOpen info
 */
const normalizeChildren = (children: React.ReactNode): NormalizedChild[] => {
  const normalized: NormalizedChild[] = []
  let index = 0

  Children.forEach(children, child => {
    if (!isValidElement(child)) return

    // Extract props from the child (could be Block or BlockItem)
    const props = child.props as {
      defaultOpen?: boolean
      collapsible?: boolean
      [key: string]: unknown
    }

    const value = `block-item-${index}`
    // If defaultOpen is explicitly set, use it. Otherwise:
    // - If collapsible=false (static block), open by default
    // - If collapsible=true or undefined (collapsible block), close by default
    const defaultOpen = props.defaultOpen ?? (props.collapsible ?? true) === false

    // Clone element with auto-generated value and key (passed internally to BlockItem)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const element = cloneElement(child, { ...props, value, key: value } as any)

    normalized.push({ element, value, defaultOpen })
    index++
  })

  return normalized
}

export const BlockListRoot: FC<BlockAccordionInternalProps> = ({
  children,
  size,
  layoutVariant = 'separated',
  gap,
  allowMultiple = true,
  onValueChange,
  disabled,
}) => {
  const contextValue = useBlockContextValue(allowMultiple, size, layoutVariant)
  const effectiveSize = contextValue.size
  const effectiveLayoutVariant = contextValue.layoutVariant

  const commonClassName = cn(
    'block',
    effectiveSize === 'md' && 'block--md',
    effectiveSize === 'sm' && 'block--sm',
    effectiveLayoutVariant === 'separated' && 'block--separated',
    effectiveLayoutVariant === 'compact' && 'block--compact-layout',
    gap === 'sm' && 'block--gap-sm',
    gap === 'md' && 'block--gap-md',
    gap === 'lg' && 'block--gap-lg',
  )

  // Normalize children and compute defaultValue from defaultOpen props
  const { normalizedChildren, computedDefaultValue } = useMemo(() => {
    const normalized = normalizeChildren(children)
    const openValues = normalized.filter(child => child.defaultOpen).map(child => child.value)

    let computedValue: string | string[] | undefined

    if (allowMultiple) {
      computedValue = openValues.length > 0 ? openValues : undefined
    } else {
      // Single mode: take the first defaultOpen
      computedValue = openValues.length > 0 ? openValues[0] : undefined
    }

    return {
      normalizedChildren: normalized.map(child => child.element),
      computedDefaultValue: computedValue,
    }
  }, [children, allowMultiple])

  const inListValue = true

  return (
    <BlockContext value={contextValue}>
      <BlockListContext.Provider value={inListValue}>
        {allowMultiple ? (
          <RadixAccordionRoot
            type="multiple"
            disabled={disabled}
            defaultValue={Array.isArray(computedDefaultValue) ? computedDefaultValue : undefined}
            onValueChange={onValueChange as ((value: string[]) => void) | undefined}
            className={commonClassName}
          >
            {normalizedChildren}
          </RadixAccordionRoot>
        ) : (
          <RadixAccordionRoot
            type="single"
            collapsible
            disabled={disabled}
            defaultValue={
              typeof computedDefaultValue === 'string' ? computedDefaultValue : undefined
            }
            onValueChange={onValueChange as (value: string) => void}
            className={commonClassName}
          >
            {normalizedChildren}
          </RadixAccordionRoot>
        )}
      </BlockListContext.Provider>
    </BlockContext>
  )
}

const getContentVariant = (parentContext: BlockContextValue): BlockContentVariant => {
  if (parentContext.depth >= 0) {
    return parentContext.contentVariant === 'soft' ? 'accent' : 'soft'
  }
  return parentContext.contentVariant
}

const getSize = (parentContext: BlockContextValue, sizeOverride?: BlockSize): BlockSize => {
  if (sizeOverride !== undefined) {
    return sizeOverride
  }
  if (parentContext.depth >= 0) {
    return 'sm'
  }
  return 'md'
}

const getLayoutVariant = (
  parentContext: BlockContextValue,
  layoutVariantOverride?: BlockLayoutVariant,
): BlockLayoutVariant => {
  if (layoutVariantOverride !== undefined) {
    return layoutVariantOverride
  }
  return parentContext.layoutVariant
}

const useBlockContextValue = (
  allowMultiple: boolean,
  sizeOverride?: BlockSize,
  layoutVariantOverride?: BlockLayoutVariant,
) => {
  const parentContext = useContext(BlockContext)
  const depth = parentContext.depth + 1
  const contentVariant = getContentVariant(parentContext)
  const size = getSize(parentContext, sizeOverride)
  const layoutVariant = getLayoutVariant(parentContext, layoutVariantOverride)

  return useMemo<BlockContextValue>(
    () => ({ size, layoutVariant, allowMultiple, depth, contentVariant }),
    [size, layoutVariant, allowMultiple, depth, contentVariant],
  )
}
